import { useMemo } from 'react';
import { HeartIcon } from '@heroicons/react/24/outline';
import CloseIcon from '@heroicons/react/24/outline/XMarkIcon';
import classNames from 'classnames';
import CartSlideout from 'components/gruenland/organisms/cart-slideout/CartSlideout';
import Wishlist from 'components/gruenland/organisms/wishlist/Wishlist';
import CartIcon from 'components/icons/cart';
import { useFormat } from 'helpers/hooks/useFormat';
import { LineItem as CartLineItem, Cart as CartShape, Discount } from 'types/entity/cart';
import { LineItem as WishlistLineItem, Wishlist as WishlistShape } from 'types/entity/wishlist';

export type MenuState = 'wishlist' | 'cart';

export interface SlideOutProps {
  cart?: CartShape;
  isEmpty?: boolean;
  onApplyDiscountCode?: (code: string) => Promise<void>;
  onRemoveDiscountCode?: (discount: Discount) => Promise<void>;
  totalCartItems?: number;
  totalWishlistItems?: number;
  onRemoveItem(lineItem: CartLineItem): Promise<void>;
  onUpdateItem(itemId: string, quantity: number): Promise<void>;
  wishlist?: WishlistShape;
  onRemoveFromWishlist?: (lineItemId: string) => Promise<void>;
  onMoveToCart?: (lineItem: WishlistLineItem) => Promise<void>;
  onClearWishlist?: () => Promise<void>;
  state?: MenuState;
  changeState?: (newState?: MenuState) => void;
  onClose?: () => void;
  enableCartState?: boolean;
  enableWishlistState?: boolean;
}

const Slideout: React.FC<SlideOutProps> = ({
  cart,
  isEmpty,
  totalCartItems = 0,
  onApplyDiscountCode,
  onRemoveDiscountCode,
  totalWishlistItems = 0,
  onRemoveItem,
  onUpdateItem,
  wishlist,
  onRemoveFromWishlist,
  onMoveToCart,
  onClearWishlist,
  state,
  changeState,
  onClose,
  enableCartState = true,
  enableWishlistState = true,
}) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });

  const title = useMemo(() => {
    switch (state) {
      case 'cart':
        return formatMessage({ id: 'cart.title', defaultMessage: 'Carrello' });
      case 'wishlist':
        return formatMessage({ id: 'wishlist.title', defaultMessage: 'Lista dei desideri' });
    }
  }, [formatMessage, state]);

  const iconClassName = 'absolute -bottom-6 left-1/2 -translate-x-1/2 h-2 w-[130%] transition duration-200';

  const wishlistClassName = classNames(iconClassName, {
    'bg-secondary-grey ease-out': state === 'wishlist',
    'bg-transparent ease-in': state !== 'wishlist',
  });

  const cartClassName = classNames(iconClassName, {
    'bg-secondary-grey ease-out': state === 'cart',
    'bg-transparent ease-in': state !== 'cart',
  });

  const ActiveSection = useMemo(
    () =>
      ({
        cart: (
          <CartSlideout
            cart={cart}
            isEmpty={isEmpty}
            onApplyDiscountCode={onApplyDiscountCode}
            onRemoveDiscountCode={onRemoveDiscountCode}
            onRemoveItem={onRemoveItem}
            onUpdateItem={onUpdateItem}
          />
        ),
        wishlist: (
          <Wishlist
            wishlist={wishlist}
            onRemoveFromWishlist={onRemoveFromWishlist}
            onMoveToCart={onMoveToCart}
            onClearWishlist={onClearWishlist}
          />
        ),
      })[state as MenuState] ?? <></>,
    [
      cart,
      isEmpty,
      onApplyDiscountCode,
      onRemoveDiscountCode,
      onRemoveItem,
      onUpdateItem,
      wishlist,
      onRemoveFromWishlist,
      onMoveToCart,
      onClearWishlist,
      state,
    ],
  );

  return (
    <>
      <div className="flex items-center justify-between border-b border-neutral-400 py-6 pb-[22px] pl-3 pr-2 md:pl-[22px] md:pr-[18px]">
        <h3 className="text-[18px] font-medium leading-normal md:text-[20px]">{title}</h3>
        <div className="flex h-full items-center gap-6">
          {enableWishlistState && (
            <div
              className="relative h-full cursor-pointer transition hover:opacity-80"
              onClick={() => changeState?.('wishlist')}
            >
              <div className={wishlistClassName} />
              {totalWishlistItems > 0 && (
                <span className="absolute right-[-8px] top-[-3px] size-[10px] rounded-full bg-primary-green" />
              )}
              <HeartIcon className="w-7" stroke="#494949" />
            </div>
          )}
          {enableCartState && (
            <div
              className="relative h-full cursor-pointer transition hover:opacity-80"
              onClick={() => changeState?.('cart')}
            >
              <>
                <div className={cartClassName} />
                <div className="relative">
                  <CartIcon className="w-7" totalCartItems={totalCartItems} counterClassName="-translate-y-1/4" />
                </div>
              </>
            </div>
          )}
          <div onClick={onClose} className="cursor-pointer transition hover:opacity-80">
            <CloseIcon className="w-7" stroke="#494949" />
          </div>
        </div>
      </div>
      {ActiveSection}
    </>
  );
};

export default Slideout;
